import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Collapse,
  Container,
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  ArrowForwardRounded,
  ExpandLess,
  ExpandMore,
  Search,
  WarningRounded,
} from "@mui/icons-material";

import SyntaxHighlighter from "react-syntax-highlighter";

import {
  adminSearchOrders,
  getLocations,
  getLocationOrdersList,
  getOrder,
  TAPI,
  TLocation,
  TOrdersList,
  useAPI,
} from "../api.js";
import {
  doesTokenAllowShowOrder,
  doesTokenAllowShowOrdersList,
} from "../auth.js";

import { TAsyncResult, useAsyncValue } from "../useAsyncValue.js";
import {
  ChangeEventHandler,
  FormEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { TPageRoute, TRoute } from "../types.js";
import { OrderDetailsSections } from "./OrderDetailsSections.js";
import { RequestErrorToast } from "../ToastMessage.js";
import { ErrorBoundary } from "./ErrorBoundary.js";
import { useTranslation } from "react-i18next";
import { OrdersFilter } from "./OrdersFilter.js";
import { OrderListItem } from "./OrderListItem.js";

const NoResultsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  "& > *:nth-child(2)": {
    marginLeft: theme.spacing(1),
  },
}));
const SearchFormRoot = styled("form")(({ theme }) => ({
  display: "grid",
  gridAutoFlow: "column",
  gridTemplateColumns: "auto min-content",
}));

export function OrderDetailsTitle({
  routeParams: { orderId },
}: {
  dataResult: TAsyncResult<null>;
  routeParams: { orderId?: string; tenantKey: string };
}) {
  const { t } = useTranslation();
  return (
    <>
      {orderId
        ? `${t("common.order")} #${orderId}`
        : t("order_details.find_order")}
    </>
  );
}

export function OrderDetailsContent({
  routeParams: { orderId, tenantKey },
}: {
  dataResult: TAsyncResult<null>;
  routeParams: { orderId?: string | undefined; tenantKey: string };
}) {
  const { t } = useTranslation();
  const api = useAPI();
  const [searchValue, setSearchValue] = useState(orderId ?? "");
  const [selectedLocation, setSelectedLocation] = useState<string>("");
  const [searchKey, setSearchKey] = useState<string>("orderID")

  const handleSearchInputChange = useCallback<
    ChangeEventHandler<HTMLInputElement>
  >((event) => setSearchValue(event.target.value), []);

  const handleFormSubmit = useCallback<FormEventHandler>(
    (event) => {
      event.preventDefault();

      if (selectedLocation != "" && isShowingAllOrdersAllowed) {
        const fetchSearchedOrders = async () => {
          try {
            const orders = await adminSearchOrders(
              api,
              tenantKey,
              selectedLocation,
              searchKey,
              searchValue,
            );
            setOrdersPromise(Promise.resolve(orders));
          } catch (error) {
            setOrdersPromise(Promise.reject(error));
          }
        };

        fetchSearchedOrders();
      }
    },
    [searchKey, searchValue, tenantKey, selectedLocation],
  );

  const [locationsResponse, setLocationsResponse] = useState<
    TLocation[] | null
  >(null);
  const [ordersPromise, setOrdersPromise] = useState<Promise<
    TOrdersList[] | { error: string }
  > | null>(null);

  useEffect(() => {
    async function fetchLocations() {
      const data = await getLocations(api, tenantKey);
      setLocationsResponse(data);

      if (data.length > 0) {
        setSelectedLocation(data[0].locationID);
      }
    }

    fetchLocations();
  }, [tenantKey, api]);

  useEffect(() => {
    async function loadOrdersForLocation(){
      try {
        const orders = await getLocationOrdersList(
          api,
          tenantKey,
          selectedLocation,
        );
        setOrdersPromise(Promise.resolve(orders));
      } catch (error) {
        setOrdersPromise(Promise.reject(error));
      }
    }

    if (!['', 'all'].includes(selectedLocation)) {
      loadOrdersForLocation()
    }
  }, [selectedLocation])

  const handleLocationFilterChange = useCallback((locationId: string) => {
    setSelectedLocation(locationId);
  }, []);

  const handleKeyFilterChange = useCallback((selectedKey: string) => {
    setSearchKey(selectedKey);
  }, [])

  const ordersList = useAsyncValue(ordersPromise);

  const isShowingOrdersAllowed = useMemo(
    () => doesTokenAllowShowOrder(api.getPermissions()),
    [api],
  );

  const isShowingAllOrdersAllowed =
    locationsResponse != null && locationsResponse.length > 0
      ? doesTokenAllowShowOrdersList(api.getPermissions(), selectedLocation)
      : null;

  const promiseGetOrder = useMemo(
    () =>
      orderId && isShowingOrdersAllowed
        ? getOrder(api, tenantKey, orderId)
        : null,
    [api, isShowingOrdersAllowed, orderId, tenantKey],
  );
  const asyncResultGetOrder = useAsyncValue(promiseGetOrder);

  const [isUpcomingOrdersCollapsed, setIsUpcomingOrdersCollapsed] =
    useState(false);
  const [isPrevOrdersCollapsed, setIsPrevOrdersCollapsed] = useState(true);
  const [isJSONCollapsed, setIsJSONCollapsed] = useState(true);

  function previousOrders() {
    if (ordersList.type === "RESOLVED" && ordersList.value instanceof Array) {
      const currentTime = new Date().getTime();
      return ordersList.value.filter(
        (od) => od.order.raw.endTime < currentTime,
      );
    }
  }

  function upcomingOrders() {
    if (ordersList.type === "RESOLVED" && ordersList.value instanceof Array) {
      const currentTime = new Date().getTime();
      return ordersList.value.filter(
        (od) => od.order.raw.endTime >= currentTime,
      );
    }
  }

  function isRegiondoOrder(source: string) {
    return source === "Regiondo";
  }

  return (
    <Box sx={{ paddingTop: "24px", paddingBottom: "24px" }}>
      <Container>
        <SearchFormRoot onSubmit={handleFormSubmit}>
          <FormControl sx={{ width: "100%" }}>
            <InputLabel htmlFor="order-details-page-search-input">
              {t("order_details.order_id")}
            </InputLabel>
            <Input
              id="order-details-page-search-input"
              disabled={!isShowingOrdersAllowed}
              value={searchValue}
              startAdornment={
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              }
              endAdornment={
                <IconButton
                  color="primary"
                  type="submit"
                  disabled={searchValue === ""}
                  size="large"
                >
                  <ArrowForwardRounded />
                </IconButton>
              }
              onChange={handleSearchInputChange}
            />
          </FormControl>
        </SearchFormRoot>

        {orderId == undefined &&
          (locationsResponse != null &&
          locationsResponse.length != 0 &&
          isShowingAllOrdersAllowed ? (
            <>
              <OrdersFilter
                selectedLocation={selectedLocation}
                searchKey={searchKey}
                availableLocations={locationsResponse}
                handleLocationChange={handleLocationFilterChange}
                handleSearchKeyChange={handleKeyFilterChange}
              />

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "20px",
                  justifyContent: "center",
                  marginTop: 4,
                }}
              >
                {selectedLocation != "" && ordersList.type === "PENDING" && (
                  <CircularProgress />
                )}
                {selectedLocation != "" &&
                  ordersList.type === "RESOLVED" &&
                  (ordersList.value instanceof Array ? (
                    ordersList.value.length == 0 ? (
                      <NoResultsContainer>
                        <WarningRounded color="error" />
                        <Typography variant="subtitle1" color="error">
                          {t("order_details.no_orders_available")}
                        </Typography>
                      </NoResultsContainer>
                    ) : (
                      <>
                        <Card sx={{ width: "100%", mt: 2 }}>
                          <CardHeader
                            title={
                              <Box
                                sx={{
                                  padding: "none",
                                  display: "flex",
                                  alignItems: "center",
                                  "& > *:first-child": {
                                    flex: "1",
                                  },
                                }}
                              >
                                <span>
                                  {t("order_details.upcoming_orders")}
                                </span>
                                {isUpcomingOrdersCollapsed ? (
                                  <ExpandMore />
                                ) : (
                                  <ExpandLess />
                                )}
                              </Box>
                            }
                            onClick={() =>
                              setIsUpcomingOrdersCollapsed(
                                (current) => !current,
                              )
                            }
                            sx={{ cursor: "pointer" }}
                          />
                          <Collapse in={!isUpcomingOrdersCollapsed}>
                            {upcomingOrders()?.map((order: TOrdersList) => {
                              return (
                                <CardContent key={order.order.id}>
                                  <OrderListItem
                                    props={order}
                                    routeParams={{
                                      orderId: order.order.id,
                                      tenantKey: tenantKey,
                                    }}
                                  />
                                </CardContent>
                              );
                            })}
                          </Collapse>
                        </Card>

                        <Card sx={{ width: "100%", mt: 2 }}>
                          <CardHeader
                            title={
                              <Box
                                sx={{
                                  padding: "none",
                                  display: "flex",
                                  alignItems: "center",
                                  "& > *:first-child": {
                                    flex: "1",
                                  },
                                }}
                              >
                                <span>
                                  {t("order_details.previous_orders")}
                                </span>
                                {isPrevOrdersCollapsed ? (
                                  <ExpandMore />
                                ) : (
                                  <ExpandLess />
                                )}
                              </Box>
                            }
                            onClick={() =>
                              setIsPrevOrdersCollapsed((current) => !current)
                            }
                            sx={{ cursor: "pointer" }}
                          />
                          <Collapse in={!isPrevOrdersCollapsed}>
                            {previousOrders()?.map((order: TOrdersList) => {
                              return (
                                <CardContent>
                                  <OrderListItem
                                    props={order}
                                    routeParams={{
                                      orderId: order.order.id,
                                      tenantKey: tenantKey,
                                    }}
                                  />
                                </CardContent>
                              );
                            })}
                          </Collapse>
                        </Card>
                      </>
                    )
                  ) : (
                    <NoResultsContainer>
                      <WarningRounded color="error" />
                      <Typography variant="subtitle1" color="error">
                        {ordersList.value.error}
                      </Typography>
                    </NoResultsContainer>
                  ))}
                {ordersList.type === "REJECTED" && (
                  <RequestErrorToast requestError={ordersList.error} />
                )}
              </Box>
            </>
          ) : (
            <NoResultsContainer>
              <WarningRounded color="error" />
              <Typography variant="subtitle1" color="error">
                {t("order_details.no_orders_list_permission")}
              </Typography>
            </NoResultsContainer>
          ))}

        {!isShowingOrdersAllowed ? (
          <NoResultsContainer>
            <WarningRounded color="error" />
            <Typography variant="subtitle1" color="error">
              {t("order_details.no_viewing_permission")}
            </Typography>
          </NoResultsContainer>
        ) : (
          <>
            {asyncResultGetOrder.type === "PENDING" && (
              <NoResultsContainer>
                <CircularProgress />
              </NoResultsContainer>
            )}
            {asyncResultGetOrder.type === "REJECTED" && (
              <NoResultsContainer>
                <WarningRounded color="error" />
                <Typography variant="subtitle1" color="error">
                  {t("order_details.order_not_found")}
                </Typography>
              </NoResultsContainer>
            )}
          </>
        )}
        {asyncResultGetOrder.type === "RESOLVED" && (
          <>
            {isRegiondoOrder(asyncResultGetOrder.value.tikiOrder.source) && (
              <ErrorBoundary>
                <OrderDetailsSections order={asyncResultGetOrder.value} />
              </ErrorBoundary>
            )}

            <Card sx={{ width: "100%", mt: 2 }}>
              <CardHeader
                title={
                  <Box
                    sx={{
                      padding: "none",
                      display: "flex",
                      alignItems: "center",
                      "& > *:first-child": {
                        flex: "1",
                      },
                    }}
                  >
                    <span>JSON</span>
                    {isJSONCollapsed ? <ExpandMore /> : <ExpandLess />}
                  </Box>
                }
                onClick={() => setIsJSONCollapsed((current) => !current)}
                sx={{ cursor: "pointer" }}
              />
              <Collapse in={!isJSONCollapsed}>
                <CardContent>
                  <SyntaxHighlighter language="json">
                    {JSON.stringify(asyncResultGetOrder.value, null, 2)}
                  </SyntaxHighlighter>
                </CardContent>
              </Collapse>
            </Card>
          </>
        )}
      </Container>
    </Box>
  );
}

export const tenantOrdersRoute: TPageRoute<
  null,
  { orderId: string; tenantKey: string }
> = {
  path: "/tenants/:tenantKey/orders",
  backPath: (params) => `/tenants/${params?.tenantKey}`,
  Title: OrderDetailsTitle,
  Content: OrderDetailsContent,
};

export const ordersRoute: TRoute<null, { orderId: string }> = {
  path: "/orders",
  redirect: (params, selectedTenant) =>
    selectedTenant ? `/tenants/${selectedTenant}/orders` : null,
};

export const tenantOrderDetailsRoute: TRoute<
  null,
  { tenantKey: string; orderId: string }
> = {
  path: "/tenants/:tenantKey/orders/:orderId",
  backPath: (params) => `/tenants/${params?.tenantKey}/orders`,
  Title: OrderDetailsTitle,
  Content: OrderDetailsContent,
};

export const orderDetailsRoute: TRoute<null, { orderId: string }> = {
  path: "/orders/:orderId",
  redirect: ({ orderId }, selectedTenant) =>
    selectedTenant
      ? {
          to: `/tenants/${selectedTenant}/orders/${orderId}`,
          replace: true,
        }
      : null,
};
